import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { FaDownload, FaUpload, FaUndo, FaTrash, FaPlay, FaShieldAlt, FaExclamationTriangle, FaHistory } from 'react-icons/fa';

/**
 * AdminBackupsPage
 *
 * Lists every backup file on disk, lets admin create new ones, download,
 * upload, restore, and delete. Restore is gated behind an explicit
 * confirmation modal that requires typing "RESTORE" verbatim so it can
 * never be triggered accidentally.
 */
const AdminBackupsPage = () => {
  const [backups, setBackups] = useState([]);
  const [retention, setRetention] = useState(10);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState(false); // disables buttons during create/restore/etc
  const [confirmTarget, setConfirmTarget] = useState(null); // backup currently being confirmed for restore
  const [confirmText, setConfirmText] = useState('');
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);

  const auth = { withCredentials: true };

  const fetchBackups = async () => {
    try {
      const r = await axios.get(`${API}/admin/backups`, auth);
      setBackups(r.data.backups || []);
      setRetention(r.data.retention || 10);
    } catch (e) {
      toast.error('Kunne ikke hente backups');
    } finally {
      setLoading(false);
    }
  };

  const fetchAudit = async () => {
    try {
      const r = await axios.get(`${API}/admin/backups/audit`, auth);
      setAudit(r.data.audit || []);
    } catch (e) {
      toast.error('Kunne ikke hente audit log');
    }
  };

  useEffect(() => {
    fetchBackups();
  }, []);

  const handleCreate = async () => {
    if (!window.confirm('Opret en ny manuel backup nu?')) return;
    setBusyAction(true);
    try {
      const r = await axios.post(`${API}/admin/backups`, {}, auth);
      toast.success(`Backup oprettet (${r.data.doc_count} dokumenter, ${formatBytes(r.data.size_bytes)})`);
      fetchBackups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke oprette backup');
    } finally {
      setBusyAction(false);
    }
  };

  const handleDownload = (filename) => {
    // Use a direct anchor so the file streams; cookies are sent automatically because withCredentials=true is the axios default for our app
    const url = `${API}/admin/backups/${filename}/download`;
    window.open(url, '_blank');
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Slet backup-fil "${filename}"?`)) return;
    setBusyAction(true);
    try {
      await axios.delete(`${API}/admin/backups/${filename}`, auth);
      toast.success('Backup slettet');
      fetchBackups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke slette');
    } finally {
      setBusyAction(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json.gz')) {
      toast.error('Filen skal være en .json.gz backup');
      e.target.value = '';
      return;
    }
    setBusyAction(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/admin/backups/upload`, fd, {
        ...auth,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Backup uploadet: ${r.data.filename}`);
      fetchBackups();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Upload fejlede');
    } finally {
      setBusyAction(false);
      e.target.value = '';
    }
  };

  const openRestoreConfirm = (b) => {
    setConfirmTarget(b);
    setConfirmText('');
  };

  const handleRestoreConfirmed = async () => {
    if (confirmText !== 'RESTORE') return;
    const filename = confirmTarget.filename;
    setBusyAction(true);
    try {
      const r = await axios.post(
        `${API}/admin/backups/${filename}/restore`,
        { confirm: 'RESTORE' },
        auth
      );
      toast.success(`Restore fuldført. Sikkerhedsbackup: ${r.data.safety_backup}`);
      setConfirmTarget(null);
      setConfirmText('');
      fetchBackups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore fejlede');
    } finally {
      setBusyAction(false);
    }
  };

  const formatBytes = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('da-DK', {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    } catch (_) { return iso; }
  };

  const kindStyle = (kind) => {
    switch (kind) {
      case 'auto': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'manual': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'pre_restore': return 'bg-amber-100 text-amber-800 border-amber-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const kindLabel = (kind) => {
    switch (kind) {
      case 'auto': return 'Dagligt auto';
      case 'manual': return 'Manuel';
      case 'pre_restore': return 'Sikkerheds-backup';
      default: return kind;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="admin-backups-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <FaShieldAlt className="text-cyan-500" />
          Backup & Restore
        </h1>
        <p className="text-gray-600 mt-2">
          Automatisk daglig backup kl. 03:00 UTC. Kun de seneste {retention} auto/manuel-backups beholdes.
          Sikkerhedsbackups (taget før hver restore) beholdes altid.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow p-4 mb-6 flex flex-wrap gap-3">
        <button
          onClick={handleCreate}
          disabled={busyAction}
          data-testid="create-backup-btn"
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-lg hover:from-emerald-600 hover:to-cyan-600 transition-all active:scale-95 disabled:opacity-50"
        >
          <FaPlay />
          Opret backup nu
        </button>

        <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors">
          <FaUpload />
          Upload backup-fil
          <input
            type="file"
            accept=".gz,.json.gz,application/gzip"
            onChange={handleUpload}
            disabled={busyAction}
            className="hidden"
            data-testid="upload-backup-input"
          />
        </label>

        <button
          onClick={() => { setShowAudit(true); fetchAudit(); }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors ml-auto"
          data-testid="audit-btn"
        >
          <FaHistory />
          Restore-historik
        </button>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="text-left p-4">Fil</th>
              <th className="text-left p-4">Type</th>
              <th className="text-left p-4">Oprettet</th>
              <th className="text-left p-4">Størrelse</th>
              <th className="text-right p-4">Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {backups.length === 0 ? (
              <tr><td colSpan="5" className="p-8 text-center text-gray-400">Ingen backups endnu. Klik "Opret backup nu" for at lave den første.</td></tr>
            ) : (
              backups.map(b => (
                <tr key={b.filename} data-testid={`backup-row-${b.filename}`} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="p-4 font-mono text-xs text-gray-700 break-all">{b.filename}</td>
                  <td className="p-4">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${kindStyle(b.kind)}`}>
                      {kindLabel(b.kind)}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-gray-600">{formatDate(b.created_at)}</td>
                  <td className="p-4 text-sm text-gray-600">{formatBytes(b.size_bytes)}</td>
                  <td className="p-4">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => handleDownload(b.filename)}
                        title="Download"
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        data-testid={`download-btn-${b.filename}`}
                      >
                        <FaDownload />
                      </button>
                      <button
                        onClick={() => openRestoreConfirm(b)}
                        title="Restore"
                        disabled={busyAction}
                        className="p-2 text-amber-600 hover:bg-amber-50 rounded disabled:opacity-50"
                        data-testid={`restore-btn-${b.filename}`}
                      >
                        <FaUndo />
                      </button>
                      <button
                        onClick={() => handleDelete(b.filename)}
                        title="Slet"
                        disabled={busyAction}
                        className="p-2 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                        data-testid={`delete-btn-${b.filename}`}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Restore confirmation modal */}
      {confirmTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <FaExclamationTriangle className="text-3xl text-red-500" />
              <h2 className="text-xl font-bold">Bekræft restore</h2>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-900 font-semibold mb-2">⚠️ Denne handling overskriver eksisterende data</p>
              <p className="text-xs text-red-800">
                Alle collections der findes i backuppen vil få deres indhold erstattet. Brugere skal logge ind igen.
                Der laves automatisk en <strong>sikkerhedsbackup</strong> af nuværende database umiddelbart inden restore starter,
                så du kan rulle tilbage hvis noget går galt.
              </p>
            </div>
            <div className="text-sm text-gray-700 mb-4 space-y-1">
              <div><strong>Fil:</strong> <code className="text-xs">{confirmTarget.filename}</code></div>
              <div><strong>Type:</strong> {kindLabel(confirmTarget.kind)}</div>
              <div><strong>Oprettet:</strong> {formatDate(confirmTarget.created_at)}</div>
              <div><strong>Størrelse:</strong> {formatBytes(confirmTarget.size_bytes)}</div>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Skriv <strong className="text-red-600">RESTORE</strong> for at bekræfte:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoFocus
              data-testid="restore-confirm-input"
              className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none font-mono"
              placeholder="RESTORE"
            />
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => { setConfirmTarget(null); setConfirmText(''); }}
                disabled={busyAction}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
              >
                Annuller
              </button>
              <button
                onClick={handleRestoreConfirmed}
                disabled={confirmText !== 'RESTORE' || busyAction}
                data-testid="restore-confirm-btn"
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busyAction ? 'Genskaber…' : 'Restore nu'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit log modal */}
      {showAudit && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2"><FaHistory /> Restore-historik</h2>
              <button onClick={() => setShowAudit(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            {audit.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Ingen restore-handlinger endnu.</p>
            ) : (
              <ul className="space-y-3">
                {audit.map((a, i) => (
                  <li key={i} className="border border-gray-200 rounded-lg p-3 text-sm">
                    <div className="font-semibold text-gray-900">{a.actor || 'ukendt'}</div>
                    <div className="text-xs text-gray-500 mb-2">{formatDate(a.restored_at)}</div>
                    <div className="text-xs">
                      <div>Restored fra: <code>{a.restored_filename}</code></div>
                      <div>Sikkerheds-backup: <code>{a.safety_backup}</code></div>
                      <div>Collections gendannet: {Object.keys(a.collections_restored || {}).length}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBackupsPage;
